import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { useAppContext } from '../../../../providers/AppProvider';
const ChartWorkbenchThemeContext = createContext(null);
function readAppChartWbTheme() {
    if (typeof document === 'undefined')
        return 'light';
    return document.documentElement.getAttribute('data-bs-theme') === 'dark' ? 'dark' : 'light';
}
/** 跟 ERP 顶栏主题（含 auto → prefers-color-scheme）对齐，不再单独记一份 */
function useAppSyncedChartWbTheme() {
    const reduxIsDark = useSelector((state) => Boolean(state.config?.isDark));
    const { changeTheme } = useAppContext();
    const [theme, setTheme] = useState(readAppChartWbTheme);
    useEffect(() => {
        setTheme(reduxIsDark ? 'dark' : 'light');
    }, [reduxIsDark]);
    useEffect(() => {
        const root = document.documentElement;
        const sync = () => {
            setTheme(root.getAttribute('data-bs-theme') === 'dark' ? 'dark' : 'light');
        };
        const observer = new MutationObserver(sync);
        observer.observe(root, { attributes: true, attributeFilter: ['data-bs-theme'] });
        return () => observer.disconnect();
    }, []);
    const toggleTheme = useCallback(() => {
        changeTheme?.(theme === 'dark' ? 'light' : 'dark');
    }, [changeTheme, theme]);
    return useMemo(() => ({
        theme,
        isLight: theme === 'light',
        isDark: theme === 'dark',
        toggleTheme,
    }), [theme, toggleTheme]);
}
/** 仅在图表工作台 / 图表库壳内有值；统计页其余图表继续跟 App 主题 */
export function useChartWorkbenchThemeOptional() {
    return useContext(ChartWorkbenchThemeContext);
}
export function ChartWorkbenchThemeRoot({ children, className, }) {
    const value = useAppSyncedChartWbTheme();
    return (<ChartWorkbenchThemeContext.Provider value={value}>
			<div className={['chart-wb-demo', 'chart-wb-theme-root', className].filter(Boolean).join(' ')} data-wb-theme={value.theme}>
				{children}
			</div>
		</ChartWorkbenchThemeContext.Provider>);
}
/** 壳层全局深浅色切换：走 ERP `changeTheme`，工作台与顶栏保持同一份 */
export function ChartWorkbenchThemeToggle({ className }) {
    const value = useChartWorkbenchThemeOptional();
    if (!value)
        return null;
    const { isDark, toggleTheme } = value;
    return (<button type="button" className={className} onClick={toggleTheme} aria-label={isDark ? '切换到浅色主题' : '切换到深色主题'} title={isDark ? '浅色主题' : '深色主题'}>
			{isDark ? (<svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
					<circle cx="12" cy="12" r="4"/>
					<line x1="12" y1="2" x2="12" y2="5"/>
					<line x1="12" y1="19" x2="12" y2="22"/>
					<line x1="4.22" y1="4.22" x2="6.34" y2="6.34"/>
					<line x1="17.66" y1="17.66" x2="19.78" y2="19.78"/>
					<line x1="2" y1="12" x2="5" y2="12"/>
					<line x1="19" y1="12" x2="22" y2="12"/>
					<line x1="4.22" y1="19.78" x2="6.34" y2="17.66"/>
					<line x1="17.66" y1="6.34" x2="19.78" y2="4.22"/>
				</svg>) : (<svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
					<path d="M21 12.79A9 9 0 1 1 11.21 3 7 7 0 0 0 21 12.79z"/>
				</svg>)}
		</button>);
}
